import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
// @ts-ignore
import {
	ArrowRight,
	ChartBar,
	CursorClick,
	Envelope,
	EnvelopeOpen,
	Globe,
	Info,
	PaperPlaneTilt,
	UserMinus,
	Warning,
} from '@phosphor-icons/react';
import {auth} from '../../firebase';
import {SigninForm} from '../molecules/SigninForm';
import {useSortableTable, SortHeader} from './_lib';

import 'leaflet/dist/leaflet.css';
import {CircleMarker, MapContainer, Popup, TileLayer} from 'react-leaflet';

// New REST API — explicitly NOT routed through getAxiosBaseUrl() so the
// dashboard can't accidentally hit the legacy applyjobs.ai:5001 surface
// while we migrate other screens.
const NEUGENCE_API_BASE = 'https://app.neugence.com';

const PERIODS = [
	{key: '1d', label: '1d'},
	{key: '2d', label: '2d'},
	{key: '3d', label: '3d'},
	{key: '7d', label: '7d'},
	{key: '30d', label: '30d'},
	{key: '90d', label: '90d'},
];

async function bearerHeaders() {
	const user = auth && auth.currentUser;
	if (!user) throw new Error('Not signed in');
	const token = await user.getIdToken();
	return {Authorization: `Bearer ${token}`};
}

function fmtNumber(n) {
	if (n == null || Number.isNaN(n)) return '—';
	if (typeof n !== 'number') return String(n);
	return n.toLocaleString();
}

function fmtRate(numer, denom) {
	if (!denom || denom <= 0) return '—';
	const pct = (numer * 100.0) / denom;
	if (Number.isNaN(pct)) return '—';
	return `${pct.toFixed(1)}%`;
}

function StatCard({icon: Icon, label, value, sub}) {
	return (
		<div className="rounded-2xl shadow-sm border border-slate-100 bg-white p-6 flex flex-col gap-3">
			<div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
				<Icon size={20} weight="duotone" color="#10B981"/>
			</div>
			<div>
				<div className="text-3xl font-semibold text-slate-900">{value}</div>
				<div className="text-sm text-slate-600 mt-1">{label}</div>
				{sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
			</div>
		</div>
	);
}

function StatCardSkeleton() {
	return (
		<div className="rounded-2xl shadow-sm border border-slate-100 bg-white p-6">
			<div className="w-10 h-10 rounded-xl bg-slate-100 animate-pulse mb-3"/>
			<div className="h-7 w-24 bg-slate-100 rounded animate-pulse mb-2"/>
			<div className="h-4 w-32 bg-slate-100 rounded animate-pulse"/>
		</div>
	);
}

function StatusBadge({status}) {
	const map = {
		Running: 'bg-emerald-100 text-emerald-800',
		Done: 'bg-slate-200 text-slate-800',
		Paused: 'bg-amber-100 text-amber-800',
		Draft: 'bg-slate-100 text-slate-700',
	};
	const cls = map[status] || 'bg-slate-100 text-slate-700';
	return (
		<span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
			{status || 'Draft'}
		</span>
	);
}

export default function Dashboard() {
	const navigate = useNavigate();
	const [period, setPeriod] = useState('7d');
	const [stats, setStats] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const loadStats = useCallback(async (currentPeriod) => {
		setLoading(true);
		setError(null);
		try {
			const headers = await bearerHeaders();
			const res = await axios.get(
				`${NEUGENCE_API_BASE}/api/v1/stats?period=${encodeURIComponent(currentPeriod)}`,
				{headers},
			);
			setStats((res && res.data) || {});
		} catch (err) {
			console.log('dashboard stats error', err);
			setError(err && err.message ? err.message : 'Could not load stats');
			setStats(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!signedIn) return;
		loadStats(period);
	}, [period, signedIn, loadStats]);

	const {
		campaignsSent,
		emailsSent,
		opens,
		clicks,
		bounces,
		unsubscribes,
		topCampaigns,
		isEmpty,
		openRate,
		clickRate,
		bounceRate,
		geoClusters,
	} = useMemo(() => {
		const s = stats || {};
		const cs = Number(s.campaigns_sent) || 0;
		const es = Number(s.emails_sent) || 0;
		const op = Number(s.opens) || 0;
		const cl = Number(s.clicks) || 0;
		const bo = Number(s.bounces) || 0;
		const un = Number(s.unsubscribes) || 0;
		const tc = Array.isArray(s.top_campaigns) ? s.top_campaigns : [];
		const gc = Array.isArray(s.geo_clusters) ? s.geo_clusters : [];
		return {
			campaignsSent: cs,
			emailsSent: es,
			opens: op,
			clicks: cl,
			bounces: bo,
			unsubscribes: un,
			openRate: typeof s.open_rate === 'number' ? s.open_rate : null,
			clickRate: typeof s.click_rate === 'number' ? s.click_rate : null,
			bounceRate: typeof s.bounce_rate === 'number' ? s.bounce_rate : null,
			topCampaigns: tc.slice(0, 10),
			geoClusters: gc,
			// Truly empty: no campaigns and no emails ever recorded for this window.
			isEmpty: cs === 0 && es === 0 && tc.length === 0,
		};
	}, [stats]);

	// Normalize Top Campaigns rows so the sort hook can sort by rate columns
	// (open_rate / click_rate / bounce_rate) even when the API only returns
	// raw counts. Adds _-prefixed numeric mirror keys.
	const topCampaignsNormalized = useMemo(() => (topCampaigns || []).map((c) => {
		const sent = Number(c.sent) || 0;
		const o = Number(c.opens) || 0, cl = Number(c.clicks) || 0, b = Number(c.bounces) || 0;
		return {
			...c,
			_sent: sent,
			_opens: o, _clicks: cl, _bounces: b,
			_open_rate: c.open_rate != null ? Number(c.open_rate) : (sent ? (o / sent) * 100 : 0),
			_click_rate: c.click_rate != null ? Number(c.click_rate) : (sent ? (cl / sent) * 100 : 0),
			_bounce_rate: c.bounce_rate != null ? Number(c.bounce_rate) : (sent ? (b / sent) * 100 : 0),
		};
	}), [topCampaigns]);
	const topCampaignsSort = useSortableTable(topCampaignsNormalized, '_sent', 'desc');

	if (!signedIn) {
		return (
			<div style={{marginTop: '300px'}}>
				<SigninForm/>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-6xl mx-auto px-6 py-10">

				{/* ---------- Quick-nav strip ---------- */}
				<div className="flex flex-wrap gap-2 mb-6 text-xs">
					<button onClick={() => navigate('/dashboard/hot')} className="rounded-full bg-rose-50 hover:bg-rose-100 border border-rose-200 px-3 py-1.5 text-rose-800 font-medium">🔥 Hot recipients</button>
					<button onClick={() => navigate('/dashboard/replies')} className="rounded-full bg-sky-50 hover:bg-sky-100 border border-sky-200 px-3 py-1.5 text-sky-800 font-medium">💬 Replies</button>
					<button onClick={() => navigate('/dashboard/personas')} className="rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-1.5 text-emerald-800 font-medium">📊 Persona funnels</button>
					<button onClick={() => navigate('/dashboard/timing')} className="rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 text-amber-800 font-medium">🕐 Timing</button>
					<button onClick={() => navigate('/dashboard/domains')} className="rounded-full bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1.5 text-slate-700 font-medium">🌐 Domains</button>
					<button onClick={() => navigate('/dashboard/suppression')} className="rounded-full bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1.5 text-slate-700 font-medium">🚫 Suppression</button>
					<button onClick={() => navigate('/dashboard/sending-status')} className="rounded-full bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1.5 text-slate-700 font-medium">✉️ SES status</button>
					<button onClick={() => navigate('/dashboard/prospects')} className="rounded-full bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1.5 text-slate-700 font-medium">👥 Prospects</button>
					<button onClick={() => navigate('/dashboard/posthog')} className="rounded-full bg-violet-50 hover:bg-violet-100 border border-violet-200 px-3 py-1.5 text-violet-800 font-medium">📈 PostHog sessions</button>
				</div>

				{/* ---------- Header ---------- */}
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
					<div>
						<h1 className="text-3xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
						<p className="text-sm text-slate-500 mt-1">
							How your campaigns are performing across the last {period.replace('d', ' days')}.
						</p>
					</div>
					<div
						role="tablist"
						aria-label="Reporting period"
						className="inline-flex rounded-full bg-slate-100 p-1 self-start md:self-auto">
						{PERIODS.map((p) => {
							const active = p.key === period;
							return (
								<button
									key={p.key}
									type="button"
									role="tab"
									aria-selected={active}
									onClick={() => setPeriod(p.key)}
									className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
										active
											? 'bg-white text-slate-900 shadow-sm'
											: 'text-slate-600 hover:text-slate-900'
									}`}>
									{p.label}
								</button>
							);
						})}
					</div>
				</div>

				{/* ---------- Error state ---------- */}
				{error && !loading && (
					<div className="rounded-2xl shadow-sm border border-amber-200 bg-amber-50 p-6 mb-8 flex items-start gap-3">
						<Warning size={24} weight="duotone" color="#D97706"/>
						<div className="flex-1">
							<h3 className="text-sm font-semibold text-amber-900">Couldn't load stats</h3>
							<p className="text-sm text-amber-800 mt-0.5">{error}</p>
						</div>
						<button
							type="button"
							onClick={() => loadStats(period)}
							className="bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 rounded-full px-4 py-2 text-sm font-medium transition-colors">
							Retry
						</button>
					</div>
				)}

				{/* ---------- Stat cards ---------- */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
					{loading ? (
						<>
							<StatCardSkeleton/>
							<StatCardSkeleton/>
							<StatCardSkeleton/>
							<StatCardSkeleton/>
						</>
					) : (
						<>
							<StatCard
								icon={PaperPlaneTilt}
								label="Campaigns sent"
								value={fmtNumber(campaignsSent)}/>
							<StatCard
								icon={Envelope}
								label="Emails delivered"
								value={fmtNumber(emailsSent - bounces > 0 ? emailsSent - bounces : emailsSent)}
								sub={bounces > 0 ? `${fmtNumber(bounces)} bounced` : null}/>
							<StatCard
								icon={EnvelopeOpen}
								label="Open rate"
								value={fmtRate(opens, emailsSent)}
								sub={opens > 0 ? `${fmtNumber(opens)} opens` : null}/>
							<StatCard
								icon={CursorClick}
								label="Click rate"
								value={fmtRate(clicks, emailsSent)}
								sub={clicks > 0 ? `${fmtNumber(clicks)} clicks` : null}/>
						</>
					)}
				</div>

				{/* ---------- Empty state (only when not loading + no error) ---------- */}
				{!loading && !error && isEmpty && (
					<div className="rounded-2xl shadow-sm border border-slate-100 bg-white p-12 text-center mb-8">
						<div className="w-14 h-14 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
							<ChartBar size={26} weight="duotone" color="#10B981"/>
						</div>
						<h3 className="text-lg font-semibold text-slate-900 mb-1">No data yet</h3>
						<p className="text-sm text-slate-600 mb-5">Send your first campaign to start collecting opens, clicks, and bounces.</p>
						<button
							type="button"
							onClick={() => navigate('/')}
							className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-3 font-semibold shadow-sm transition-colors">
							Send your first campaign
						</button>
					</div>
				)}

				{/* ---------- Top campaigns table ---------- */}
				{!loading && !error && !isEmpty && (
					<div className="rounded-2xl shadow-sm border border-slate-100 bg-white overflow-hidden mb-8">
						<div className="px-6 py-4 border-b border-slate-100">
							<h2 className="text-sm font-semibold text-slate-900">Top campaigns</h2>
							<p className="text-xs text-slate-500 mt-0.5">Top {topCampaigns.length} by emails sent in this window.</p>
						</div>
						{topCampaigns.length === 0 ? (
							<div className="px-6 py-10 text-center text-sm text-slate-500">
								No campaign sends recorded for this period yet.
							</div>
						) : (
							<table className="w-full text-sm">
								<thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
									<tr>
										<SortHeader {...topCampaignsSort} column="name" className="text-left font-medium px-6 py-3">Campaign</SortHeader>
										<SortHeader {...topCampaignsSort} column="_sent" numeric className="text-right font-medium px-6 py-3">Sent</SortHeader>
										<SortHeader {...topCampaignsSort} column="_open_rate" numeric className="text-right font-medium px-6 py-3">Open rate</SortHeader>
										<SortHeader {...topCampaignsSort} column="_click_rate" numeric className="text-right font-medium px-6 py-3">Click rate</SortHeader>
										<SortHeader {...topCampaignsSort} column="_bounce_rate" numeric className="text-right font-medium px-6 py-3">Bounce rate</SortHeader>
									</tr>
								</thead>
								<tbody>
									{topCampaignsSort.rows.map((c) => {
										const sent = Number(c.sent) || 0;
										const cOpens = Number(c.opens) || 0;
										const cClicks = Number(c.clicks) || 0;
										const cBounces = Number(c.bounces) || 0;
										return (
											<tr
												key={c.id || c.name}
												onClick={() => c.id && navigate(`/dashboard/campaigns/${encodeURIComponent(c.id)}`)}
												className="border-t border-slate-100 hover:bg-emerald-50/30 cursor-pointer transition-colors">
												<td className="px-6 py-4">
													<div className="flex items-center gap-2">
														<span className="font-medium text-slate-900">{c.name || 'Untitled'}</span>
														{c.status && <StatusBadge status={c.status}/>}
													</div>
												</td>
												<td className="px-6 py-4 text-right text-slate-700">{fmtNumber(sent)}</td>
												<td className="px-6 py-4 text-right text-slate-700">
													{c.open_rate != null ? `${c.open_rate}%` : fmtRate(cOpens, sent)}
													<span className="block text-xs text-slate-400 mt-0.5">{fmtNumber(cOpens)} opens</span>
												</td>
												<td className="px-6 py-4 text-right text-slate-700">
													{c.click_rate != null ? `${c.click_rate}%` : fmtRate(cClicks, sent)}
													<span className="block text-xs text-slate-400 mt-0.5">{fmtNumber(cClicks)} clicks</span>
												</td>
												<td className="px-6 py-4 text-right text-slate-700">
													{c.bounce_rate != null ? `${c.bounce_rate}%` : fmtRate(cBounces, sent)}
													<span className="block text-xs text-slate-400 mt-0.5">{fmtNumber(cBounces)} bounces</span>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						)}
					</div>
				)}

				{/* ---------- World map of opens / clicks ---------- */}
				{!loading && !error && !isEmpty && (
					<div className="rounded-2xl shadow-sm border border-slate-100 bg-white overflow-hidden mb-8">
						<div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
							<Globe size={18} weight="duotone" color="#10B981"/>
							<div>
								<h2 className="text-sm font-semibold text-slate-900">Where opens are happening</h2>
								<p className="text-xs text-slate-500 mt-0.5">
									Aggregated across your campaigns in this window. Open events are
									indicative — Apple MPP and Gmail proxy distort geo. Clicks are real signal.
								</p>
							</div>
						</div>
						{geoClusters && geoClusters.length > 0 ? (
							<div style={{height: 420}}>
								<MapContainer
									center={[20, 0]}
									zoom={2}
									minZoom={2}
									style={{height: '100%', width: '100%'}}
									scrollWheelZoom={false}
									worldCopyJump={true}>
									<TileLayer
										attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
										url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
									/>
									{geoClusters.map((c, i) => {
										if (c.lat == null || c.lng == null) return null;
										const radius = Math.min(20, 4 + Math.log2((c.count || 1) + 1) * 2);
										const isClick = c.type === 'click';
										const color = isClick ? '#ef4444' : '#f59e0b';
										return (
											<CircleMarker
												key={i}
												center={[c.lat, c.lng]}
												radius={radius}
												pathOptions={{
													color,
													fillColor: color,
													fillOpacity: isClick ? 0.7 : 0.35,
													weight: isClick ? 2 : 1,
												}}>
												<Popup>
													<div className="text-xs">
														<div className="font-semibold">{isClick ? 'Click' : 'Open'}</div>
														<div>{c.city || c.country || '(unknown)'}</div>
														<div>{c.count} event{c.count === 1 ? '' : 's'}</div>
													</div>
												</Popup>
											</CircleMarker>
										);
									})}
								</MapContainer>
							</div>
						) : (
							<div className="px-6 py-12 text-center text-sm text-slate-500">
								No geo-tagged opens or clicks yet for this window.
							</div>
						)}
					</div>
				)}

				{/* ---------- Footer: suppression + identities ---------- */}
				{!loading && !error && (
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="rounded-2xl shadow-sm border border-slate-100 bg-white p-6 flex items-start gap-4">
							<div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
								<UserMinus size={20} weight="duotone" color="#10B981"/>
							</div>
							<div className="flex-1">
								<h3 className="text-sm font-semibold text-slate-900">Suppression list</h3>
								<p className="text-xs text-slate-500 mt-0.5">Recipients who unsubscribed in this period.</p>
								<div className="text-2xl font-semibold text-slate-900 mt-2">{fmtNumber(unsubscribes)}</div>
							</div>
						</div>
						<div className="rounded-2xl shadow-sm border border-slate-100 bg-white p-6 flex items-start gap-4">
							<div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
								<Envelope size={20} weight="duotone" color="#10B981"/>
							</div>
							<div className="flex-1">
								<h3 className="text-sm font-semibold text-slate-900">Sender identities</h3>
								<p className="text-xs text-slate-500 mt-0.5">Add or verify the addresses you send from.</p>
								<button
									type="button"
									onClick={() => navigate('/identities')}
									className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700">
									Manage senders <ArrowRight size={14} weight="bold"/>
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
